/**
 * Custom hook for managing project data
 */

import { useEffect, useState } from 'react';

import { fetchProjectBySlug, fetchProjects } from '../services/api/project';
import { Project, ProjectListResponse } from '../services/types/project';

export interface UseProjectsReturn {
  data: ProjectListResponse;
  loading: boolean;
  error: string | null;
  refetch: (params?: {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
    isFeatured?: boolean;
  }) => Promise<void>;
}

/**
 * Hook for fetching projects with pagination and filtering
 */
export function useProjects(params?: {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  isFeatured?: boolean;
}): UseProjectsReturn {
  const [data, setData] = useState<ProjectListResponse>({
    data: [],
    meta: {
      pagination: {
        page: 1,
        pageSize: 9,
        pageCount: 0,
        total: 0,
      },
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (fetchParams?: {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
    isFeatured?: boolean;
  }) => {
    try {
      setLoading(true);
      setError(null);

      console.log('useProjects: Fetching with params:', fetchParams || params);
      const result = await fetchProjects(fetchParams || params);

      console.log('useProjects: Received data:', result);
      setData(result);
    } catch (err) {
      console.error('useProjects: Error fetching projects:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch projects';
      setError(errorMessage);

      // Set empty data on error
      setData({
        data: [],
        meta: {
          pagination: {
            page: 1,
            pageSize: 9,
            pageCount: 0,
            total: 0,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useProjects: Effect triggered with params:', params);
    fetchData(params);
  }, [params?.page, params?.pageSize, params?.isActive, params?.isFeatured]);

  const refetch = async (refetchParams?: {
    page?: number;
    pageSize?: number;
    isActive?: boolean;
    isFeatured?: boolean;
  }) => {
    console.log('useProjects: Refetching with params:', refetchParams);
    await fetchData(refetchParams);
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
}

export interface UseProjectDetailReturn {
  project: Project | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching a single project by slug
 */
export function useProjectDetail(slug: string): UseProjectDetailReturn {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('useProjectDetail: Fetching project with slug:', slug);
      const projectResult = await fetchProjectBySlug(slug);
      setProject(projectResult.data);
    } catch (err) {
      console.error('useProjectDetail: Error fetching project:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch project';
      setError(errorMessage);
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      console.log('useProjectDetail: Effect triggered with slug:', slug);
      fetchData();
    }
  }, [slug]);

  const refetch = async () => {
    console.log('useProjectDetail: Refetching project');
    await fetchData();
  };

  return {
    project,
    loading,
    error,
    refetch,
  };
}
